/**
 * Overview KPI cards 2–4 — dynamic answers from site intent + report outcomes.
 */
import type { ProjectIntent } from '../../types/siteRag';
import type { SiteRagReport } from '../../types/siteRag';
import type { SiteEntity } from '../../types/site';
import type { ReportTypeId } from '../../config/reportTypes';
import { REPORT_TYPE_REGISTRY } from '../../config/reportTypes';
import type { RankedChecklistMatch } from '../../config/documentChecklists';
import { getDocumentChecklistAcross } from '../../config/documentChecklists';
import type { DerivedSiteVerdict } from '../portfolio/deriveSiteVerdict';
import { classifyRequirementDomain, type CoverageRequirementDomain } from '../coverage/requirementDomainGroups';
import {
  computeRequirementStatusAcrossTypes,
  type RequirementStatus,
} from '../coverage/requirementStatus';
import { mostActionableReport, truncateToChars } from '../siteCardSummary';
import { firstReportFinding } from '../reportFindings';
import { getDisplayVerdict } from '../reportVerdict';
import { effectivePower, effectiveTimeline } from '../projectIntent';
import { hasDroughtExposedSource } from '../intent/waterSource';
import { getWorkloadProfile, effectiveWorkloadProfile } from '../workloadProfile';
import { buildOverviewVerdictSummary } from './buildOverviewVerdictSummary';

export type FindingReadiness = 'Strong' | 'Adequate' | 'Tentative';

export interface OverviewFindingCard {
  displayValue: string;
  subtext: string;
  readiness: FindingReadiness;
  progress: number;
  /** When set, Overview can deep-link to this report. */
  reportId: string | null;
}

/** One intent-critical requirement row and its current coverage status. */
export interface IntentMissingRow {
  id: string;
  label: string;
  domain: CoverageRequirementDomain;
  status: RequirementStatus;
  /**
   * Uploaded documents that plausibly cover this requirement (ranked DCT
   * match, an explicit upload-for-this-requirement, or a tag-pattern hit).
   * When non-empty the gap is a VERIFY/EXTRACT gap, not an UPLOAD gap —
   * surfaces must not ask the user to upload a document they already
   * provided. Optional so legacy producers stay byte-stable.
   */
  matchedDocs?: readonly { docId: string; fileName: string }[];
}

/** Per-phase planning-data coverage (capacity + in-service date defined). */
export interface IntentPhaseCoverage {
  phaseId: string;
  phaseLabel: string;
  covered: number;
  total: number;
}

/**
 * Intent-evidence card plus the explicit gap detail the Overview gap view
 * renders: the full list of still-missing intent-critical rows and, when the
 * intent is phased, a per-phase planning-data coverage summary.
 */
export interface IntentEvidenceCard extends OverviewFindingCard {
  /** Every intent-critical requirement not yet at a complete status. */
  missingRows: IntentMissingRow[];
  /** Total intent-critical requirements walked. */
  totalRows: number;
  /** Completed (>= adequate / authored) intent-critical requirements. */
  completedRows: number;
  /** Per-phase planning-data coverage; empty when intent is not phased. */
  phaseCoverage: IntentPhaseCoverage[];
}

const COMPLETE_STATUSES: RequirementStatus[] = [
  'authored-strong',
  'strong',
  'authored',
  'adequate',
];

function readinessFromProgress(pct: number, hasBlocker: boolean): FindingReadiness {
  if (hasBlocker) return 'Tentative';
  if (pct >= 75) return 'Strong';
  if (pct >= 45) return 'Adequate';
  return 'Tentative';
}

/** Report types and domains that matter for the declared site intent. */
export function intentCriticalScope(intent: ProjectIntent | undefined): {
  reportTypes: ReportTypeId[];
  domains: CoverageRequirementDomain[];
} {
  // Water Resource is required of every site (P1.2, `ALWAYS_REQUIRED_FOR_GO`),
  // so its evidence is intent-critical for every site — not only liquid-cooled
  // or drought-exposed ones (review M1, 2026-09-05).
  // Two-stage Phase 3 (2026-09-06): environmental and financial are required
  // of every site (`ALWAYS_REQUIRED_FOR_GO`), and supply chain of every site
  // with an on-site power path — so their evidence is intent-critical for
  // every site too (superset invariant with the required set).
  // Phase 3 part B (2026-09-07): `water-resource` is retired — its screen is a
  // feasibility chapter, so the `water` DOMAIN stays intent-critical for every
  // site while no water REPORT is ever asked for.
  const reportTypes = new Set<ReportTypeId>([
    'feasibility', 'power-availability', 'environmental', 'financial', 'supply-chain-risk',
  ]);
  const domains = new Set<CoverageRequirementDomain>(['site', 'grid', 'water', 'environmental']);

  if (!intent) {
    return { reportTypes: [...reportTypes], domains: [...domains] };
  }

  if (intent.nuclearInterest === 'primary' || intent.nuclearInterest === 'considering') {
    reportTypes.add('interconnection');
    reportTypes.add('supply-chain-risk');
  }
  if (intent.priority === 'sustainability') {
    reportTypes.add('environmental');
    domains.add('environmental');
  }
  if (intent.priority === 'speed' || intent.priority === 'resilience') {
    reportTypes.add('interconnection');
  }
  if (intent.coolingStrategy === 'liquid' || intent.coolingStrategy === 'hybrid') {
    domains.add('water');
  }
  if (intent.priority === 'cost') {
    reportTypes.add('test-fit');
  }

  // ── BTM intent fields widen the intent-critical scope (field-architecture
  // audit §4). A declared exposure on a BTM axis makes the report-type that
  // diligences it critical, so its missing-evidence rows surface. Signal-gated
  // on an EXPLICIT exposed value — an absent / 'none' / undecided field is NOT a
  // signal and leaves the scope byte-stable (the no-override invariant).
  if (hasDroughtExposedSource(intent.waterSource)) {
    domains.add('water');
  }
  const q = intent.interconnectionStatus?.queueStatus;
  if (q === 'pre-application' || q === 'queued' || q === 'study') {
    reportTypes.add('interconnection');
  }
  const fuel = intent.fuelAccess?.supply;
  if (fuel === 'lateral-required' || fuel === 'none') {
    reportTypes.add('supply-chain-risk');
  }
  const cci = intent.capacityCreditIntent?.target;
  if (cci === 'partial' || cci === 'firm') {
    reportTypes.add('power-availability'); // already seeded, idempotent
  }

  return { reportTypes: [...reportTypes], domains: [...domains] };
}

/** Per-phase planning-data coverage: capacity + in-service date defined. */
function computePhaseCoverage(
  phases: import('../../types/siteRag').ProjectPhase[] | undefined,
): IntentPhaseCoverage[] {
  if (!phases || phases.length === 0) return [];
  return phases.map(phase => {
    let covered = 0;
    if (phase.capacityMW != null) covered += 1;
    if (phase.targetInServiceDate != null || phase.codTimeline != null) covered += 1;
    return {
      phaseId: phase.id,
      phaseLabel: phase.label,
      covered,
      total: 2,
    };
  });
}

export function computeIntentEvidenceCard(args: {
  projectIntent?: ProjectIntent;
  matchByType: ReadonlyMap<string, RankedChecklistMatch>;
  documents: import('../../types/siteRag').SiteDocument[];
  sed?: import('../../types/siteEvidence').SiteEvidenceDocument;
  authoredClaims?: readonly import('../../types/siteContext').ExtractedClaim[];
}): IntentEvidenceCard {
  const { projectIntent, matchByType, documents, sed, authoredClaims } = args;

  if (!projectIntent) {
    return {
      displayValue: 'Define intent first',
      subtext: 'Set workload and power to scope evidence',
      readiness: 'Tentative',
      progress: 0,
      reportId: null,
      missingRows: [],
      totalRows: 0,
      completedRows: 0,
      phaseCoverage: [],
    };
  }

  const { reportTypes, domains } = intentCriticalScope(projectIntent);
  const requirements = getDocumentChecklistAcross(reportTypes).filter(r =>
    domains.includes(classifyRequirementDomain(r)),
  );

  const filteredMatch = new Map(
    [...matchByType.entries()].filter(([t]) => reportTypes.includes(t as ReportTypeId)),
  );

  const statuses = requirements.map(req =>
    computeRequirementStatusAcrossTypes({
      requirement: req,
      documents,
      sed,
      authoredClaims,
      matchByType: filteredMatch,
    }),
  );

  const completed = statuses.filter(s => COMPLETE_STATUSES.includes(s)).length;
  const progress = Math.round((completed / requirements.length) * 100);
  const firstMissing = requirements.find((req, i) => !COMPLETE_STATUSES.includes(statuses[i]!));

  const missingRows: IntentMissingRow[] = requirements
    .map((req, i) => ({ req, status: statuses[i]! }))
    .filter(({ status }) => !COMPLETE_STATUSES.includes(status))
    .map(({ req, status }) => ({
      id: req.id,
      label: req.label,
      domain: classifyRequirementDomain(req),
      status,
    }));

  let displayValue: string;
  if (progress >= 75) {
    displayValue = 'Evidence supports intent';
  } else if (firstMissing) {
    displayValue = truncateToChars(`Gap: ${firstMissing.label}`, 44);
  } else {
    displayValue = 'Building evidence base';
  }

  return {
    displayValue,
    subtext: `${completed} / ${requirements.length} intent-critical rows`,
    readiness: readinessFromProgress(progress, progress < 45 && !!firstMissing),
    progress,
    reportId: null,
    missingRows,
    totalRows: requirements.length,
    completedRows: completed,
    phaseCoverage: computePhaseCoverage(projectIntent.phases),
  };
}

export function computeTopConstraintCard(args: {
  site: Pick<SiteEntity, 'id' | 'name' | 'reportIds'>;
  projectIntent?: ProjectIntent;
  siteReports: SiteRagReport[];
}): OverviewFindingCard {
  const { site, projectIntent, siteReports } = args;
  const report = mostActionableReport(site as SiteEntity, siteReports);

  if (!report) {
    const power = effectivePower(projectIntent);
    const timeline = effectiveTimeline(projectIntent);
    const gaps: string[] = [];
    if (power.bucket === 'undecided' && !power.isExact) gaps.push('power target');
    if (!timeline.isExact && timeline.bucket === 'undecided') gaps.push('in-service date');
    const displayValue = gaps.length > 0
      ? truncateToChars(`Set ${gaps.join(' & ')}`, 44)
      : 'Run feasibility first';
    return {
      displayValue,
      subtext: 'No report findings yet',
      readiness: 'Tentative',
      progress: projectIntent ? 25 : 0,
      reportId: null,
    };
  }

  const finding = firstReportFinding(report);
  const typeLabel = REPORT_TYPE_REGISTRY[(report.reportType ?? 'feasibility') as ReportTypeId]?.shortLabel ?? 'Report';
  const verdict = getDisplayVerdict(report);
  const rec = report.conclusion?.recommendation?.trim();

  const displayValue = finding
    ? truncateToChars(finding, 48)
    : rec
      ? truncateToChars(rec, 48)
      : verdict.tier !== 'none'
        ? verdict.label
        : 'Review report';

  const subtextParts = [typeLabel];
  if (verdict.tier !== 'none') subtextParts.push(verdict.label);
  if (rec && finding) subtextParts.push(truncateToChars(rec, 36));

  // P1.4 — a withheld headline is tentative evidence, never a strong one.
  const rank = report.overallStatus === 'red' || report.overallStatus === 'withheld'
    ? 0
    : report.overallStatus === 'amber' ? 1 : 2;
  const readiness: FindingReadiness = rank === 0 ? 'Tentative' : rank === 1 ? 'Adequate' : 'Strong';
  const progress = rank === 0 ? 25 : rank === 1 ? 55 : 85;

  return {
    displayValue,
    subtext: subtextParts.join(' · '),
    readiness,
    progress,
    reportId: report.id,
  };
}

export function computeSiteFitCard(args: {
  projectIntent?: ProjectIntent;
  verdict: DerivedSiteVerdict | null;
  siteReports: SiteRagReport[];
}): OverviewFindingCard {
  const { projectIntent, verdict, siteReports } = args;
  const summary = buildOverviewVerdictSummary(verdict, projectIntent);
  const workload = getWorkloadProfile(effectiveWorkloadProfile(projectIntent ?? undefined));

  if (!verdict?.band && siteReports.length === 0) {
    return {
      displayValue: 'Fit not scored',
      subtext: `Intent: ${workload.label} — add reports`,
      readiness: 'Tentative',
      progress: 0,
      reportId: null,
    };
  }

  if (!verdict?.band) {
    const latest = [...siteReports].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )[0];
    const v = latest ? getDisplayVerdict(latest) : null;
    return {
      displayValue: v?.tier !== 'none' ? v!.label : 'Narrative fit only',
      subtext: truncateToChars(summary, 56),
      readiness: v?.tier === 'green' ? 'Strong' : v?.tier === 'amber' ? 'Adequate' : 'Tentative',
      progress: v?.tier === 'green' ? 70 : v?.tier === 'amber' ? 50 : 30,
      reportId: latest?.id ?? null,
    };
  }

  const composite = verdict.composite;
  const progress =
    composite != null ? Math.round(Math.max(0, Math.min(100, (composite / 5) * 100))) : 50;

  let readiness: FindingReadiness = 'Adequate';
  if (verdict.band === 'go' || verdict.band === 'conditional-high') readiness = 'Strong';
  else if (verdict.band === 'no-go') readiness = 'Tentative';

  const displayValue = verdict.bandLabel ?? 'Site fit';
  const subtextParts: string[] = [truncateToChars(summary, 40)];
  if (composite != null) subtextParts.push(`Composite ${composite.toFixed(2)}`);
  if (verdict.confidenceTag) subtextParts.push(verdict.confidenceTag);

  return {
    displayValue,
    subtext: subtextParts.join(' · '),
    readiness,
    progress,
    reportId: null,
  };
}
